import myProfileStyle from "./my-profile.module.css";

import { useRef, useContext, useState, useEffect, useReducer, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import { UNSAFE_NavigationContext as NavigationContext, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Container, Row, Col } from "react-bootstrap";
import { QRCodeSVG } from "qrcode.react";
import FileSaver from 'file-saver';
import _ from "lodash";

// Util
import { messageKeyPrefix } from "../../../utils/message.util";
import * as UserUtil from "../../../utils/user.util";

// Component
import ButtonWithIcon from "../../../components/ButtonWithIcon";
import InlineTitle from "../../../components/InlineTitle";
import Input from "../../../components/Input";
import InputWithIcon from "../../../components/InputWithIcon";
import DropdownWithItem from "../../../components/DropdownWithItem";
import Checkbox from "../../../components/Checkbox";
import Button from "../../../components/Button";
import LinkerWithA from "../../../components/LinkerWithA";
import MessageBox from "../../../components/MessageBox";
import EditableNameBox from "../../../components/EditableNameBox";
import userImg from "../../../assets/img/v2/icon/icon_user_default.png";
import TooltipDialog from "../../../components/TooltipDialog";
import ConfirmMessageModal from "../../../components/ConfirmMessageModal";

// Hook
import usePrompt, { ConfirmMessageModal1Id } from "../../../hooks/useBlocker";

// Model
import User from "../../../models/User";
import UploadImg from './modals/UploadImg';
import RemoveImg from './modals/RemoveImg';
import DeletAccount from './modals/DeletAccount';
import GenerateOrgReport from './modals/GenerateOrgReport';

// Slice
import { uiActions } from "../../../stores/slice/ui";
import {
  fetchUser,
  setInitData,
  postWebSetting,
  putUser,
  putPassword,
  postVerify,
  getTwoFAGoogleQR,
  initVerifyValue,
  postUploadPhoto,
  deleteUploadPhoto,
  sendEmail,
  postDeleteAccount
} from "../../../stores/slice/userProfile";
import { selectManagedOrg, selectManagedSiteTag, selectManagedSite } from "../../../stores/slice/pageContext";
import { userActions } from '../../../stores/slice/user';

// Local store
import myProfileStore from "./store/store";
import MyProfileStoreCtrl from "./store/StoreCtrl";
import Nuclias from '../../../const/nuclias';

// Service
import AuthOrganizationService from '../../../services/Auth.Organization.service';

// Default value
const defaultMessages = {
  success: null,
  error: null,
  warning: null,
};

const defaulUploadFileMessages = {
  show: false,
  label: null,
};

const messageWordings = {
  twoFactorAuth: 'f3e46e0cf4',
  saveSuccess: '863eb6b72c'
};

const defaultTwoFactorAuthList = [
  { label: 'Disable', title: 'b9f5c797eb', name: 'DISABLE', value: 'DISABLE', isActive: true },
  { label: 'Email authentication', title: 'c8844983bb', name: 'EMAIL', value: 'EMAIL', isActive: false },
  { label: 'Google authenticator', title: 'b9e1a3500c', name: 'GOOGLE', value: 'TOTP', isActive: false },
];

const defaultLoginReferenceList = [
  { label: 'No default login preference', title: '6f88f9fa8b', name: 'none', value: 1, isActive: true },
  { label: 'Set Cloud Portal as default', title: '1e597349f4', name: 'toCloud', value: 2, isActive: false },
  { label: 'Set Connect Portal as default', title: '20bde89c83', name: 'toConnect', value: 3, isActive: false }
];

const defaulGenerateOrgReportMessages = {
  show: false,
  label: null,
};

const MyProfile = ({ originMyProfileData, setOriginMyProfileData, syncMyProfileData }) => {
  const { user, publicKey, userMyProfile } = useSelector((store) => store);
  const managedOrg = useSelector(selectManagedOrg(user.user.type));
  const managedSiteTag = useSelector(selectManagedSiteTag);
  const managedSite = useSelector(selectManagedSite);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation();

  // Ref
  const siteListRef = useRef();

  // useReducer
  const [myProfileState, myProfileDispatch] = useReducer(
    myProfileStore.reducer,
    myProfileStore.defaultState
  );

  MyProfileStoreCtrl.state = myProfileState;
  MyProfileStoreCtrl.action = myProfileStore.action;
  MyProfileStoreCtrl.dispatch = myProfileDispatch;

  // state
  const [originName, setOriginName] = useState('');
  const [messages, setMessages] = useState({ ...defaultMessages });
  const [isVerify, setVerify] = useState(false);
  const [isVerifyError, setVerifyError] = useState(false);
  const [selectedTwoFactorAuth, setSelectedTwoFactorAuth] = useState({ ...defaultTwoFactorAuthList[0] });
  const [twoFactorAuthList, setTwoFactorAuthList] = useState(defaultTwoFactorAuthList);
  const [isManuallyCode, setIsManuallyCode] = useState(false);
  const [selectedLoginReference, setLoginReference] = useState({ ...defaultLoginReferenceList[0] });
  const [loginReferenceList, setLoginReferenceList] = useState(defaultLoginReferenceList);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadFileMessages, setUploadFileMessages] = useState({ ...defaulUploadFileMessages });
  const [deleteUserMessages, setDeleteUserMessages] = useState(null);
  const [generateOrgReportMessages, setGenerateOrgReportMessages] = useState({ ...defaulGenerateOrgReportMessages });
  const [showOpenSiteListIcon, setShowOpenSiteListIcon] = useState(false);
  const [showOpenSiteList, setShowOpenSiteList] = useState(false);

  // Modal
  const [isOpenDeleteAccountModal, setOpenDeleteAccountModal] = useState(false);
  const [isOpenUploadImgModal, setOpenUploadImgModal] = useState(false);
  const [isRemoveImgModal, setRemoveImgModal] = useState(false);
  const [isGenerateOrgReportModal, setGenerateOrgReportModal] = useState(false);

  // init
  const init = () => {
    const initTwoFactorValue = (data, selectedItem) => {
      let returnValue = data[0];
      for (let item of data) {
        if (item.value === selectedItem) {
          item.isActive = true;
          returnValue = item;
        } else {
          item.isActive = false;
        }
      }
      setTwoFactorAuthList(data)
      return returnValue
    }

    const set2FA = () => {
      const twoFactorValue = initTwoFactorValue(defaultTwoFactorAuthList, user.user.twoFaType);
      setSelectedTwoFactorAuth(twoFactorValue);
      MyProfileStoreCtrl.changeTwoFactor(twoFactorValue);
    }

    const initLoginPreference = (userValue) => {
      let loginPreferenceValue = null;
      const others = userValue.webSetting ? userValue.webSetting.others : null;

      if (others && others.hasOwnProperty('toConnect')) {
        if (userValue.webSetting.others.toConnect) {
          loginPreferenceValue = defaultLoginReferenceList.filter(item => item.name === 'toConnect')[0];
        } else {
          loginPreferenceValue = defaultLoginReferenceList.filter(item => item.name === 'toCloud')[0];
        }
      } else {
        loginPreferenceValue = defaultLoginReferenceList.filter(item => item.name === 'none')[0];
      }

      for (let item of loginReferenceList) {
        item.isActive = item.value === loginPreferenceValue.value ? true : false;
      }
      setLoginReferenceList(loginReferenceList);
      return loginPreferenceValue;
    }

    const setLoginPreference = () => {
      const loginPreferenceValue = initLoginPreference(user.user);
      setLoginReference(loginPreferenceValue);
      MyProfileStoreCtrl.changeLoginPreference(loginPreferenceValue);
    }

    const setPassword = () => {
      MyProfileStoreCtrl.initPassword();
    }

    MyProfileStoreCtrl.setData(user.user);
    set2FA();
    setLoginPreference();
    setPassword();

    // Save origin data
    setOriginMyProfileData({
      name: user.user.name,
      twoFaType: user.user.twoFaType,
      idleTimeout: user.user.webSetting?.others?.idle_timeout || 60,
      loginPreference: initLoginPreference(user.user).value,
      photo: null
    });
  };

  const initVerify = useCallback(async () => {
    setVerifyError(false);
    setVerify(false);
    MyProfileStoreCtrl.setCode('');
    dispatch(initVerifyValue(true));
  }, [dispatch]);

  const sendProfileEmail = async () => {
    dispatch(uiActions.showSpinner());
    try {
      await dispatch(sendEmail()).unwrap();
      const newSuccessMessages = {
        ...messages,
        success: messageWordings.saveSuccess,
        error: null,
      }
      setMessages({ ...newSuccessMessages });
      dispatch(uiActions.hideSpinner());
    } catch (error) {
      const newErrorMessages = {
        ...messages,
        success: null,
        error: error,
      }
      setMessages({ ...newErrorMessages });
      dispatch(uiActions.hideSpinner());
    }
  }

  const deleteAccount = async (password) => {
    dispatch(uiActions.showSpinner());
    const deleteData = {
      userIds: [user.user.id],
      confirmPassword: User.encryptPassword(publicKey.value.publicKey, password)
    };
    try {
      await dispatch(postDeleteAccount(deleteData)).unwrap();
      User.removeTokenLocalStoreage();
      navigate('/login');
    } catch (error) {
      setDeleteUserMessages(error.code);
      dispatch(uiActions.hideSpinner());
    }
  };

  // change data
  const changeLoginPreferenceAction = (selectedItem, data) => {
    for (let item of data) {
      item.isActive = item.value === selectedItem.value ? true : false;
    }
    setLoginReferenceList(data);
  }

  const changeTwoFactorListAction = (selectedItem, data) => {
    for (let item of data) {
      item.isActive = item.value === selectedItem.value ? true : false;
    }
    setTwoFactorAuthList(data);
  }

  const postVerifyData = async (value) => {
    dispatch(uiActions.showSpinner());

    let data = { code: value.code };
    if (value) {
      dispatch(postVerify(data));
      dispatch(uiActions.hideSpinner());
    }
  }

  const getSaveUserData = () => {
    let webSettingData = {
      ...user.user.webSetting,
      others: {
        ...user.user.webSetting.others,
        idle_timeout: Number.parseInt(MyProfileStoreCtrl.state.idleTimeout),
        optInMarketingEmail: MyProfileStoreCtrl.state.optInMarketingEmail,
      }
    };

    if (MyProfileStoreCtrl.state.loginPreference.name === 'none') {
      delete webSettingData.others.toConnect;
    } else {
      webSettingData = {
        ...webSettingData,
        others: {
          ...webSettingData.others,
          toConnect: MyProfileStoreCtrl.state.loginPreference.name === 'toConnect' ? true : false,
        }
      };
    }

    let userData = {
      ...user.user,
      name: MyProfileStoreCtrl.state.name.value,
      twoFaType: MyProfileStoreCtrl.state.selectedTwoFaType.value,
      optInMarketingEmail: MyProfileStoreCtrl.state.optInMarketingEmail,
      webSetting: webSettingData
    }

    if (MyProfileStoreCtrl.state.currentPassword.isValid && MyProfileStoreCtrl.state.currentPassword.value.length > 0) {
      const changeUserDataValue = {
        ...userData,
        password: User.encryptPassword(publicKey.value.publicKey, MyProfileStoreCtrl.state.currentPassword.value),
        confirmPassword: User.encryptPassword(publicKey.value.publicKey, MyProfileStoreCtrl.state.confirmPassword.value)
      };

      userData = changeUserDataValue;
    }

    return userData;
  }

  const saveImage = async (userData) => {
    try {
      if (MyProfileStoreCtrl.state.photo.photoSource) {
        await dispatch(postUploadPhoto(MyProfileStoreCtrl.state.photo.file)).unwrap();
      }
      if (!MyProfileStoreCtrl.state.photo.photoS3Path && MyProfileStoreCtrl.state.photo.uploadPhotoStep !== 1) {
        await dispatch(deleteUploadPhoto(userData)).unwrap();
      }
    } catch (error) {
      return Promise.reject(error);
    }
  }

  const savePassword = async (userData) => {
    if (MyProfileStoreCtrl.state.currentPassword.isValid && MyProfileStoreCtrl.state.currentPassword.value !== '') {
      const passwordValue = User.encryptPassword(
        publicKey.value.publicKey,
        MyProfileStoreCtrl.state.confirmPassword.value
      );
      const confirmPasswordValue = User.encryptPassword(
        publicKey.value.publicKey,
        MyProfileStoreCtrl.state.currentPassword.value
      );

      let passwordData = {
        id: userData.id,
        password: passwordValue,
        confirmPassword: confirmPasswordValue
      }

      try {
        await dispatch(putPassword(passwordData)).unwrap();
      } catch (error) {
        return Promise.reject(error);
      }
    }
  }

  const saveData = async () => {
    dispatch(uiActions.showSpinner());
    const userData = getSaveUserData();

    try {
      await dispatch(postWebSetting(userData)).unwrap();
      await saveImage(userData);
      await dispatch(putUser(userData)).unwrap();
      await savePassword(userData);
      await dispatch(fetchUser()).unwrap();
      const newSuccessMessages = {
        ...messages,
        success: messageWordings.saveSuccess,
        error: null,
      }

      // Update
      const udpatedMyProfileData = {
        name: MyProfileStoreCtrl.state.name.value,
        twoFaType: MyProfileStoreCtrl.state.selectedTwoFaType.value,
        idleTimeout: parseInt(MyProfileStoreCtrl.state.idleTimeout),
        loginPreference: parseInt(MyProfileStoreCtrl.state.loginPreference.value),
        photo: MyProfileStoreCtrl.state.photo.photoSource
      };
      setOriginMyProfileData(udpatedMyProfileData);
      syncMyProfileData(udpatedMyProfileData);

      setMessages(newSuccessMessages);

      dispatch(uiActions.hideSpinner());
    } catch (error) {
      const newErrorMessages = {
        ...messages,
        success: null,
        error: error,
      }
      setMessages(newErrorMessages);
      dispatch(uiActions.hideSpinner());
    }
  }

  // check
  const checkIsVerify = () => {
    const data = { ...userMyProfile.userMyProfile };

    if (selectedTwoFactorAuth.name === 'GOOGLE') {
      if (data.hasOwnProperty('verifySuccess')) {
        if (isVerifyError && !isVerify) {
          return false;
        } else if (!isVerifyError && isVerify) {
          return true;
        }
      } else {
        return false
      }
    }

    return true;
  }

  const checkPassword = () => {
    if (
      MyProfileStoreCtrl.state.currentPassword.isValid &&
      MyProfileStoreCtrl.state.newPassword.isValid &&
      MyProfileStoreCtrl.state.confirmPassword.isValid
    ) {
      if (MyProfileStoreCtrl.state.currentPassword.value.length > 0) {
        if (
          MyProfileStoreCtrl.state.newPassword.value.length > 0 &&
          MyProfileStoreCtrl.state.confirmPassword.value.length > 0
        ) {
          if (MyProfileStoreCtrl.state.newPassword.checkSame || !MyProfileStoreCtrl.state.confirmPassword.checkSame) {
            return false;
          }
          else {
            return true;
          }
        }
        else {
          return false;
        }
      }

      return true;
    } else {
      return false
    }
  }

  const checkForm = () => {
    let checkVerifyValue = checkIsVerify();
    let checkPasswordValue = checkPassword();
    let checkIdleTimeoutValue = checkIdleTimeout(MyProfileStoreCtrl.state.idleTimeout);

    if (!checkVerifyValue || !checkPasswordValue || checkIdleTimeoutValue) {
      return false
    }

    return true;
  }

  const checkIdleTimeout = (idleTimeout) => {
    const timeout = Number(idleTimeout);
    return (timeout > 0 && timeout <= 60) ? false : true;
  }

  // file
  const onFileSelect = useCallback((file) => {
    const IMAGE_MAX_SIZE = 1024 * 1024;
    const fileType = ['image/png', 'image/jpeg', 'image/jpg', 'image/gif', 'image/bmp', 'image/tiff'];

    if (file) {
      const fileSize = file.size;
      let checkFileSize = fileSize > IMAGE_MAX_SIZE ? false : true;
      let checkFileType = fileType.includes(file.type) ? true : false;

      if (!checkFileSize) {
        let errorMessages = t(`${messageKeyPrefix.e.c}${1006}`);
        setUploadFileMessages({ ...defaulUploadFileMessages, show: true, label: errorMessages });
        return;
      }

      if (!checkFileType) {
        let errorMessages = t(`${messageKeyPrefix.e.s}${1005}`);
        setUploadFileMessages({ ...defaulUploadFileMessages, show: true, label: errorMessages });
        return;
      }

      if (checkFileSize && checkFileType) {
        setUploadFile(file);
      }
    }
  }, [t]);

  const openUploadImgModal = () => {
    setUploadFile(null);
    setUploadFileMessages({ ...defaulUploadFileMessages });
    setOpenUploadImgModal(true);
  }

  const openDeleteAccountModal = () => {
    setDeleteUserMessages(null);
    MyProfileStoreCtrl.initDeletePassword();
    setOpenDeleteAccountModal(true);
  }

  const onUploadFile = useCallback(async (file) => {
    const reader = new FileReader();

    if (file) {
      reader.readAsDataURL(file);
      reader.onload = (event) => {
        MyProfileStoreCtrl.uploadFile({
          event: event,
          file: file
        });
      }
    }
  }, []);

  const generateSpOrSiOrgReport = useCallback(async () => {
    await AuthOrganizationService.generateOrgReport(managedOrg.id);

    if (AuthOrganizationService.hasError()) {
      let errorMessages = t(`${messageKeyPrefix.e.s}${AuthOrganizationService.error.originCode}`);
      setGenerateOrgReportMessages({ ...defaulGenerateOrgReportMessages, show: true, label: errorMessages });
    } else {
      setGenerateOrgReportModal(false);
    }
  }, [managedOrg.id, t]);

  const downloadSpOrSiOrgReport = async () => {
    await AuthOrganizationService.downloadGeneratedOrgReport(managedOrg.id);

    if (AuthOrganizationService.hasError()) {
      const newErrorMessages = {
        ...messages,
        success: null,
        error: AuthOrganizationService.error.originCode
      }
      setMessages({ ...newErrorMessages });
    } else {
      FileSaver.saveAs(AuthOrganizationService.data.data.downloadUrl);
    }
  }

  const getManagedSiteList = useCallback(() => {
    const siteList = [];

    if (user.user.type === Nuclias.userType.org) {
      for (let siteId in managedOrg.storeObj) {
        siteList.push(managedOrg.storeObj[siteId].name)
      }
    }

    if (user.user.type === Nuclias.userType.siteTag) {
      for (let siteId in managedSiteTag.storeObj) {
        siteList.push(managedSiteTag.storeObj[siteId].name);
      }
    }

    if (user.user.type === Nuclias.userType.site) {
      siteList.push(managedSite.name);
    }

    return siteList.join(', ');
  }, [managedOrg?.storeObj, managedSite?.name, managedSiteTag?.storeObj, user.user.type]);

  // useEffect init slice
  useEffect(() => {
    // useEffect init useReducer localStorage
    dispatch(uiActions.showSpinner());
    init();
    // useEffect init redux Storage
    dispatch(setInitData(user.user));
    dispatch(uiActions.hideSpinner());
  }, []);

  // user data change
  useEffect(() => {
    dispatch(userActions.setUser(user.user));
  }, [user]);

  // useEffect get post api userMyProfile change
  useEffect(() => {
    const data = { ...userMyProfile.userMyProfile };
    if (data.QRData) {
      MyProfileStoreCtrl.setQRData({ ...data.QRData });
    }

    if (data.hasOwnProperty('verifySuccess') && data.verifySuccess) {
      setVerify(true);
      setVerifyError(false);
    } else if (data.hasOwnProperty('verifySuccess') && !data.verifySuccess) {
      setVerify(false);
      setVerifyError(true);
    }
  }, [userMyProfile]);

  // MyProfileStoreCtrl.state change
  useEffect(() => {
    if (MyProfileStoreCtrl.state) {
      setOriginName(MyProfileStoreCtrl.state.name.value);

      if (MyProfileStoreCtrl.state.photo.uploadPhotoStep === 1) {
        setOpenUploadImgModal(false);
      }
      if (MyProfileStoreCtrl.state.photo.uploadPhotoStep === -1) {
        setRemoveImgModal(false);
      }
    }
  }, [myProfileState]);

  // TwoFactorAuth change
  useEffect(() => {
    initVerify();

    if (selectedTwoFactorAuth.name === 'GOOGLE') {
      dispatch(getTwoFAGoogleQR());
      messages.warning = messageWordings.twoFactorAuth;
      setMessages({ ...messages });
    }
  }, [selectedTwoFactorAuth]);

  // Check site list str is too long to have to show open icon
  useEffect(() => {
    if (siteListRef.current?.scrollHeight > siteListRef.current?.clientHeight) {
      setShowOpenSiteListIcon(true);
    }
  }, [getManagedSiteList, showOpenSiteListIcon]);

  // Handle unsaving behavior
  // Sync changed state to parent component for changing tab
  useEffect(() => {
    syncMyProfileData({
      name: MyProfileStoreCtrl.state.name.value,
      twoFaType: MyProfileStoreCtrl.state.selectedTwoFaType?.value,
      idleTimeout: parseInt(MyProfileStoreCtrl.state.idleTimeout),
      loginPreference: MyProfileStoreCtrl.state.loginPreference.value,
      photo: MyProfileStoreCtrl.state.photo.photoSource
    });
  }, [myProfileState, originMyProfileData, syncMyProfileData]);

  // Save changed state for changing route
  const changedMyProfileData = {
    name: MyProfileStoreCtrl.state.name.value,
    twoFaType: MyProfileStoreCtrl.state.selectedTwoFaType?.value,
    idleTimeout: parseInt(MyProfileStoreCtrl.state.idleTimeout),
    loginPreference: parseInt(MyProfileStoreCtrl.state.loginPreference.value),
    photo: MyProfileStoreCtrl.state.photo.photoSource
  };

  const [stopTransition, setStopTransition] = useState(false);
  const navigatorContext = useContext(NavigationContext);
  usePrompt(stopTransition, !_.isEqual(originMyProfileData, changedMyProfileData));

  const confirmLeving = (answer) => {
    if (answer) {
      setStopTransition(true);
    } else {
      setStopTransition(false);
    }

    document.getElementById(ConfirmMessageModal1Id).hidden = true;
  }

  useEffect(() => {
    if (stopTransition) {
      navigate(navigatorContext.navigator.lastPath.pathname)
    }
  }, [stopTransition])

  return (
    <div className="layout-container layout-container--column layout-container--fluid">
      {/* My profile */}
      <InlineTitle label={t('d69a78e5bb')}>
        <ButtonWithIcon
          label={t('67b52a2d32')}
          className={myProfileStyle['email-this-page-btn']}
          iconClassName='icon-email'
          onClick={() => { sendProfileEmail() }}
        ></ButtonWithIcon>

        <ButtonWithIcon
          label={t('750f17c7cc')}
          className={myProfileStyle['delete-account-btn']}
          iconClassName='icon-trash'
          onClick={() => { openDeleteAccountModal() }}
        ></ButtonWithIcon>
      </InlineTitle>

      <MessageBox
        show={!!messages.success}
        label={t(messages.success)}
        variant='success'
        onClose={() => setMessages({ ...defaultMessages, success: null })}
      />
      <MessageBox
        show={!!messages.error}
        label={t(`${messageKeyPrefix.e.s}${messages.error}`)}
        variant='danger'
        onClose={() => setMessages({ ...defaultMessages, error: null })}
      />
      <MessageBox
        show={!!messages.warning}
        label={t(messages.warning)}
        variant='warning'
        onClose={() => setMessages({ ...defaultMessages, warning: null })}
      />

      <Container style={{ margin: 0, padding: 0 }} className='mb-5'>
        <Row lg={8}>
          <Col xl={7} lg={8}>
            <div className='user-info-container'>
              <div className='form-group'>
                <div className='form-title'>{t('49ee308734')}</div>
                <div className='form-field'>
                  <EditableNameBox
                    onClickCancelIcon={() => setOriginName(MyProfileStoreCtrl.state.name.value)}
                    inputFieldOnKeyDown={e => MyProfileStoreCtrl.changeName(e.target.value)}
                    inputFieldOnChange={e => setOriginName(e.target.value)}
                    value={originName}
                    minLength={1}
                    maxLength={64}
                  />
                </div>

              </div>

              <div className='form-group'>
                <div className='form-title'>{t('ce8ae9da5b')}</div>
                <div className='form-field'>{MyProfileStoreCtrl.state.email}</div>
              </div>
              {MyProfileStoreCtrl.state.currentPassword.isText && (
                <div className='form-group'>
                  <div className='form-title'>{t('2cd5272a18')}</div>
                  <div className='form-field'>
                    <InputWithIcon
                      type='password'
                      placeholder={t('956c37eb4c')}
                      autoComplete='new-password'
                      value={MyProfileStoreCtrl.state.currentPassword.value}
                      isInvalid={MyProfileStoreCtrl.checkPasswordIsValid('currentPassword')}
                      onChange={(e) => MyProfileStoreCtrl.changeCurrentPassword(e.target.value)}
                      iconTitle='Show password'
                      iconClassName='icon-open-eye'
                      iconOnClick={() => MyProfileStoreCtrl.toggleCurrentPasswordEye()}
                    />
                  </div>
                </div>
              )}
              {!MyProfileStoreCtrl.state.currentPassword.isText && (
                <div className='form-group'>
                  <div className='form-title'>{t('2cd5272a18')}</div>
                  <div className='form-field'>
                    <InputWithIcon
                      type='text'
                      placeholder={t('956c37eb4c')}
                      autoComplete='new-password'
                      value={MyProfileStoreCtrl.state.currentPassword.value}
                      isInvalid={MyProfileStoreCtrl.checkPasswordIsValid('currentPassword')}
                      onChange={(e) => MyProfileStoreCtrl.changeCurrentPassword(e.target.value)}
                      iconTitle='Hide password'
                      iconClassName='icon-close-eye'
                      iconOnClick={() => MyProfileStoreCtrl.toggleCurrentPasswordEye()}
                    />
                  </div>
                </div>
              )}

              {MyProfileStoreCtrl.state.newPassword.isText && (
                <div className='form-group'>
                  <div className='form-title'>{t('ae3bb2a1ac')}</div>
                  <div className='form-field'>
                    <InputWithIcon
                      type='password'
                      placeholder={t('956c37eb4c')}
                      autoComplete='new-password'
                      value={MyProfileStoreCtrl.state.newPassword.value}
                      isInvalid={MyProfileStoreCtrl.checkPasswordIsValid('newPassword')}
                      onChange={(e) => MyProfileStoreCtrl.changeNewPassword(e.target.value)}
                      iconTitle='Show password'
                      iconClassName='icon-open-eye'
                      iconOnClick={() => MyProfileStoreCtrl.toggleNewPasswordEye()}
                      onFocus={(e) => MyProfileStoreCtrl.changeNewPasswordFocus(e)}
                      onBlur={(e) => MyProfileStoreCtrl.changeNewPasswordBlur(e)}
                    />
                    {MyProfileStoreCtrl.state.newPassword.isFocus && MyProfileStoreCtrl.state.newPassword.value.length === 0 && (
                      <span className={`ms-1 ${myProfileStyle['v2-point-text']}`}>{t('5182e0c938')}</span>
                    )}
                    {!MyProfileStoreCtrl.state.newPassword.isFocus && !MyProfileStoreCtrl.state.newPassword.isValid && MyProfileStoreCtrl.state.newPassword.isDirty && (
                      <span className={`ms-1 ${myProfileStyle['v2-error-text']}`}>{t('5182e0c938')}</span>
                    )}
                    {!MyProfileStoreCtrl.state.newPassword.isFocus && MyProfileStoreCtrl.state.newPassword.checkSame && MyProfileStoreCtrl.state.newPassword.isDirty && MyProfileStoreCtrl.state.newPassword.value.length > 0 && (
                      <span className={`ms-1 ${myProfileStyle['v2-error-text']}`}>{t('45793fbc86')}</span>
                    )}
                  </div>
                </div>
              )}

              {!MyProfileStoreCtrl.state.newPassword.isText && (
                <div className='form-group'>
                  <div className='form-title'>{t('ae3bb2a1ac')}</div>
                  <div className='form-field'>
                    <InputWithIcon
                      type='text'
                      placeholder={t('956c37eb4c')}
                      autoComplete='new-password'
                      value={MyProfileStoreCtrl.state.newPassword.value}
                      isInvalid={MyProfileStoreCtrl.checkPasswordIsValid('newPassword')}
                      onChange={(e) => MyProfileStoreCtrl.changeNewPassword(e.target.value)}
                      iconTitle='Hide password'
                      iconClassName='icon-close-eye'
                      iconOnClick={() => MyProfileStoreCtrl.toggleNewPasswordEye()}
                      onFocus={(e) => MyProfileStoreCtrl.changeNewPasswordFocus(e)}
                      onBlur={(e) => MyProfileStoreCtrl.changeNewPasswordBlur(e)}
                    />
                    {MyProfileStoreCtrl.state.newPassword.isFocus && MyProfileStoreCtrl.state.newPassword.value.length === 0 && (
                      <span className={`ms-1 ${myProfileStyle['v2-point-text']}`}>{t('5182e0c938')}</span>
                    )}
                    {!MyProfileStoreCtrl.state.newPassword.isFocus && !MyProfileStoreCtrl.state.newPassword.isValid && MyProfileStoreCtrl.state.newPassword.isDirty && (
                      <span className={`ms-1 ${myProfileStyle['v2-error-text']}`}>{t('5182e0c938')}</span>
                    )}
                    {!MyProfileStoreCtrl.state.newPassword.isFocus && MyProfileStoreCtrl.state.newPassword.checkSame && MyProfileStoreCtrl.state.newPassword.isDirty && MyProfileStoreCtrl.state.newPassword.value.length > 0 && (
                      <span className={`ms-1 ${myProfileStyle['v2-error-text']}`}>{t('45793fbc86')}</span>
                    )}
                  </div>
                </div>
              )}

              {MyProfileStoreCtrl.state.confirmPassword.isText && (
                <div className='form-group'>
                  <div className='form-title'>{t('b619fecf9c')}</div>
                  <div className='form-field'>
                    <InputWithIcon
                      type='password'
                      placeholder={t('956c37eb4c')}
                      autoComplete='new-password'
                      value={MyProfileStoreCtrl.state.confirmPassword.value}
                      isInvalid={MyProfileStoreCtrl.checkPasswordIsValid('confirmPassword')}
                      onChange={(e) => MyProfileStoreCtrl.changeConfirmPassword(e.target.value)}
                      iconTitle='Show password'
                      iconClassName='icon-open-eye'
                      iconOnClick={() => MyProfileStoreCtrl.toggleConfirmPasswordEye()}
                      onFocus={(e) => MyProfileStoreCtrl.changeConfirmPasswordFocus(e)}
                      onBlur={(e) => MyProfileStoreCtrl.changeConfirmPasswordBlur(e)}
                    />
                    {MyProfileStoreCtrl.state.confirmPassword.isFocus && MyProfileStoreCtrl.state.confirmPassword.value.length === 0 && (
                      <span className={`ms-1 ${myProfileStyle['v2-point-text']}`}>{t('8f0e554d3d')}</span>
                    )}
                    {!MyProfileStoreCtrl.state.confirmPassword.isFocus && MyProfileStoreCtrl.state.confirmPassword.value.length > 0 && !MyProfileStoreCtrl.state.confirmPassword.checkSame && (
                      <span className={`ms-1 ${myProfileStyle['v2-error-text']}`}>{t('592761ebed')}</span>
                    )}
                    {!MyProfileStoreCtrl.state.confirmPassword.isFocus && MyProfileStoreCtrl.state.confirmPassword.value.length === 0 && MyProfileStoreCtrl.state.newPassword.value.length > 0 && (
                      <span className={`ms-1 ${myProfileStyle['v2-error-text']}`}>{t('592761ebed')}</span>
                    )}
                  </div>
                </div>
              )}

              {!MyProfileStoreCtrl.state.confirmPassword.isText && (
                <div className='form-group'>
                  <div className='form-title'>{t('b619fecf9c')}</div>
                  <div className='form-field'>
                    <InputWithIcon
                      type='text'
                      placeholder={t('956c37eb4c')}
                      autoComplete='new-password'
                      value={MyProfileStoreCtrl.state.confirmPassword.value}
                      isInvalid={MyProfileStoreCtrl.checkPasswordIsValid('newPassword')}
                      onChange={(e) => MyProfileStoreCtrl.changeConfirmPassword(e.target.value)}
                      iconTitle='Hide password'
                      iconClassName='icon-close-eye'
                      iconOnClick={() => MyProfileStoreCtrl.toggleConfirmPasswordEye()}
                    />
                    {MyProfileStoreCtrl.state.confirmPassword.isFocus && MyProfileStoreCtrl.state.confirmPassword.value.length === 0 && (
                      <span className={`ms-1 ${myProfileStyle['v2-point-text']}`}>{t('8f0e554d3d')}</span>
                    )}
                    {!MyProfileStoreCtrl.state.confirmPassword.isFocus && MyProfileStoreCtrl.state.confirmPassword.value.length > 0 && !MyProfileStoreCtrl.state.confirmPassword.checkSame && (
                      <span className={`ms-1 ${myProfileStyle['v2-error-text']}`}>{t('592761ebed')}</span>
                    )}
                    {!MyProfileStoreCtrl.state.confirmPassword.isFocus && MyProfileStoreCtrl.state.confirmPassword.value.length === 0 && MyProfileStoreCtrl.state.newPassword.value.length > 0 && (
                      <span className={`ms-1 ${myProfileStyle['v2-error-text']}`}>{t('592761ebed')}</span>
                    )}
                  </div>
                </div>
              )}

              <div className='form-group'>
                <div className='form-title'>{t('bfe7ba462a')}</div>
                <DropdownWithItem
                  id='two-factor-auth-dropdown'
                  type='normal'
                  selectedItem={selectedTwoFactorAuth}
                  itemList={twoFactorAuthList}
                  className={`dropdown-item ${selectedTwoFactorAuth.isActive ? "active" : ""
                    }`}
                  onClick={
                    item => {
                      const msg = item.value === 'google' ? messageWordings['twoFactorAuth'] : null;
                      messages.warning = msg;
                      setMessages({ ...messages });
                      setSelectedTwoFactorAuth(item);
                      MyProfileStoreCtrl.changeTwoFactor(item);
                      changeTwoFactorListAction(item, defaultTwoFactorAuthList);
                    }
                  }
                />
              </div>

              <div className={`form-group ${myProfileStyle['google-auth-container']} ${selectedTwoFactorAuth.name === 'GOOGLE' ? 'inherit' : 'd-none'}`}>
                <div className='form-title'></div>
                <div className='form-field'>
                  <ul>
                    <li>{t('d2a9e4981c')}</li>
                    <li className='list-style-type-none'><span>{t('9ee2e35931')}</span></li>
                    <li className={`mt-2 ${myProfileStyle['qrcode-icon']}`}>
                      <QRCodeSVG
                        value={MyProfileStoreCtrl.state.googleDOM.qrCodeUrl}
                        size={150}
                        bgColor={"#ffffff"}
                        fgColor={"#000000"}
                        level={"L"}
                        includeMargin={false}
                      />
                    </li>

                    <li className='list-style-type-none'>
                      <LinkerWithA
                        label="Can't scan the QR code?"
                        href='#'
                        className='linker-blue linker-blue-hover text-decoration-underline list-style-type-none'
                        onClick={
                          e => {
                            e.preventDefault();
                            setIsManuallyCode(!isManuallyCode);
                          }
                        }
                      />
                    </li>
                    <div className={`${!!isManuallyCode ? 'inherit' : 'd-none'}`}>
                      <li className='list-style-type-none'>Manually enter a code:</li>
                      <ol className={myProfileStyle['manually-code-list']}>
                        <li>{t('4c3e6d1aa0')}</li>
                        <li>{('a97c4168d3')} <span>{MyProfileStoreCtrl.state.googleDOM.labelStr}/{MyProfileStoreCtrl.state.googleDOM.issuerStr}</span> {t('dcc3eb1b1b')}</li>
                        <li>{t('62a0b73408')}<br />
                          <span>{MyProfileStoreCtrl.state.googleDOM.secretStr}</span></li>
                        <li>{t('26b8035436')}</li>
                        <li>{t('357f96798b')}</li>
                      </ol>
                    </div>
                    <li className='v2-two-factor-title mt-2'>{t('b20b8a1f08')}</li>
                    <li className='list-style-type-none'>{t('1f5c010544')}</li>
                    <li className='list-style-type-none'>{t('c92ab6fc57')}</li>
                    <li className={`list-style-type-none ${myProfileStyle['manually-code-container']}`}>
                      <span className='me-1'>{t('3339c3b5d0')}:</span>
                      <Input
                        type='text'
                        autoComplete='manuallly-code'
                        value={MyProfileStoreCtrl.state.code}
                        onChange={e => MyProfileStoreCtrl.setCode(e.target.value)}
                      />
                      <Button
                        label={t('fb4c4bf8e3')}
                        className='btn-indigo ms-2'
                        onClick={() => postVerifyData(MyProfileStoreCtrl.state)}
                      />

                      {isVerify && (
                        <div className={`ms-1 ${myProfileStyle['small-input-successful']}`}>{t(`${messageKeyPrefix.s.c}${1010}`)}</div>
                      )}
                      {isVerifyError && (
                        <div className={`ms-1 ${myProfileStyle['small-input-error']}`}>{t('6c3059a7b7')}</div>
                      )}
                    </li>
                  </ul>
                </div>
              </div>

              <div className='form-group'>
                <div className='form-title'>{t('d3c62e6142')}</div>
                <div className='form-field'>
                  <Input
                    type='number'
                    placeholder='1-60'
                    autoComplete='automatic-logout-time-limit'
                    value={MyProfileStoreCtrl.state.idleTimeout}
                    isInvalid={checkIdleTimeout(MyProfileStoreCtrl.state.idleTimeout)}
                    onChange={e => { MyProfileStoreCtrl.changeLogoutTime(e.target.value) }}
                  />
                </div>
                <div className='form-unit'>{t('26fba427f3')}</div>
              </div>

              <div className='form-group'>
                <div className='form-title'>{t('ccf571abf0')}</div>
                <DropdownWithItem
                  id='login-preference-dropdown'
                  type='normal'
                  selectedItem={selectedLoginReference}
                  itemList={loginReferenceList}
                  onClick={
                    item => {
                      setLoginReference({ ...item })
                      MyProfileStoreCtrl.changeLoginPreference(item)
                      changeLoginPreferenceAction(item, loginReferenceList)
                    }
                  }
                />
              </div>

              <div className='form-group'>
                <div className='form-title'></div>
                <Checkbox
                  id={myProfileStyle['receive-promotion-cb']}
                  htmlFor={myProfileStyle['receive-promotion-cb']}
                  type='checkbox'
                  label={t('2d5e656686')}
                  checked={MyProfileStoreCtrl.state.optInMarketingEmail === true}
                  onChange={e => { MyProfileStoreCtrl.clickOptInMarketingEmail(!MyProfileStoreCtrl.state.optInMarketingEmail) }}
                />
              </div>

            </div>
          </Col>
          <Col xl={3} lg={2}>
            <div className={myProfileStyle['user-img-container']}>
              {!MyProfileStoreCtrl.state.photo.photoSource && !MyProfileStoreCtrl.state.photo.photoS3Path &&
                <img id='u_profile_default_icon' className={myProfileStyle['user-img']} src={userImg} alt='' />
              }
              {MyProfileStoreCtrl.state.photo.photoS3Path && MyProfileStoreCtrl.state.photo.uploadPhotoStep !== 1 &&
                <img id='u_profile_uploaded_icon' className={myProfileStyle['user-img']} src={MyProfileStoreCtrl.state.photo.photoS3Path} alt='' />
              }
              {MyProfileStoreCtrl.state.photo.photoSource && MyProfileStoreCtrl.state.photo.uploadPhotoStep === 1 &&
                <img id='u_profile_photoSource_icon' className={myProfileStyle['user-img']} src={MyProfileStoreCtrl.state.photo.photoSource} alt='' />
              }
              <div>{t("e377b4314b")}</div>

              <div className={myProfileStyle['user-img-button-group']}>
                <Button
                  label={t('1f98842c35')}
                  className='btn-indigo'
                  onClick={() => openUploadImgModal()}
                />

                <Button
                  label={t('81d6cc0623')}
                  className='btn-indigo'
                  onClick={() => setRemoveImgModal(true)}
                />
              </div>
            </div>
          </Col>
        </Row>
      </Container>

      {/* Access privilege */}
      <InlineTitle label={t('8f1bd444d2')}></InlineTitle>
      <div className={myProfileStyle['access-privilege-container']}>
        <div className='form-group'>
          <div className='form-title form-title--label'>{t('de88250cdc')}</div>
          <div className='form-field form-field--label'>{t(UserUtil.getUserAccessLevelI18n(user.user.type))}</div>
        </div>
        <div className='form-group'>
          <div className='form-title form-title--label'>{t('727b677950')}</div>
          <div className='form-field form-field--label'>{t(UserUtil.getUserPrivilegeI18n(user.user.privilege))}</div>
        </div>
        {(user.user.type === Nuclias.userType.sp || user.user.type === Nuclias.userType.si) && (
          <div className='form-group'>
            <div className='form-title'>
              <div className='form-title--extend'>{t('c1ca926603')}</div>
              <TooltipDialog placement='right' title={t('9d53acb410')}></TooltipDialog>
            </div>
            <div className='form-field'>
              <Button
                label={t('de3e350386')}
                className={myProfileStyle['download-btn']}
                onClick={() => setGenerateOrgReportModal(true)}
              />
              <ButtonWithIcon
                label={t('801ab24683')}
                className={myProfileStyle['download-btn']}
                iconClassName='icon-download'
                onClick={() => downloadSpOrSiOrgReport()}
              />
            </div>
          </div>
        )}
        {(user.user.type !== Nuclias.userType.sp && user.user.type !== Nuclias.userType.si) && (
          <>
            <div className='form-group'>
              <div className='form-title form-title--label'>{t('c1ca926603')}</div>
              <div className='form-field form-field--label'>{managedOrg.name}</div>
            </div>
            <div className='form-group'>
              <div className='form-title form-title--label'>{t('c9772a79fb')}</div>
              <div className='form-field form-field--label'>{managedOrg.uid}</div>
            </div>
            <div className='form-group'>
              <div className='form-title form-title--label'>{t('a7d6475ec8')}</div>
              <div
                className={`form-field ${showOpenSiteList ? '.form-field--unlimit-height-label' : 'form-field--label'}`}
                ref={siteListRef}
              >
                {!showOpenSiteListIcon && getManagedSiteList()}
                {showOpenSiteListIcon && (
                  <div className={myProfileStyle['site-list']}>
                    <div className={myProfileStyle['left']}>
                      {getManagedSiteList()}
                    </div>
                    <div className={myProfileStyle['right']}>
                      <div
                        className={`${myProfileStyle['open-site-list']} ${showOpenSiteList ? myProfileStyle['open-site-list--down'] : ''}`}
                        onClick={() => setShowOpenSiteList(!showOpenSiteList)}
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>
          </>
        )}
      </div>

      <div className={myProfileStyle['apply-cancel-btn-group']}>
        <Button
          label={t('ea4788705e')}
          className='btn-cancel me-3'
          onClick={() => init()}
        />
        <Button
          label={t("c9cc8cce24")}
          className='btn-submit'
          disabled={!checkForm()}
          onClick={saveData}
        />
      </div>

      {/* Delet account modal */}
      <DeletAccount
        isOpenDeleteAccountModal={isOpenDeleteAccountModal}
        setOpenDeleteAccountModal={setOpenDeleteAccountModal}
        deleteUserMessages={deleteUserMessages}
        setDeleteUserMessages={setDeleteUserMessages}
        MyProfileStoreCtrl={MyProfileStoreCtrl}
        deleteAccount={deleteAccount}
      />

      {/* Upload img modal */}
      <UploadImg
        isOpenUploadImgModal={isOpenUploadImgModal}
        setOpenUploadImgModal={setOpenUploadImgModal}
        uploadFileMessages={uploadFileMessages}
        setUploadFileMessages={setUploadFileMessages}
        defaulUploadFileMessages={defaulUploadFileMessages}
        uploadFile={uploadFile}
        onFileSelect={onFileSelect}
        onUploadFile={onUploadFile}
      />

      {/* Remove img modal */}
      <RemoveImg
        isRemoveImgModal={isRemoveImgModal}
        setRemoveImgModal={setRemoveImgModal}
        MyProfileStoreCtrl={MyProfileStoreCtrl}
      />

      {/* Generate organization report modal */}
      <GenerateOrgReport
        isGenerateOrgReportModal={isGenerateOrgReportModal}
        setGenerateOrgReportModal={setGenerateOrgReportModal}
        generateOrgReportMessages={generateOrgReportMessages}
        setGenerateOrgReportMessages={setGenerateOrgReportMessages}
        generateSpOrSiOrgReport={generateSpOrSiOrgReport}
      />

      <ConfirmMessageModal confirmLeving={confirmLeving} />
    </div >
  )
}

export default MyProfile;